import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  authUserSchema,
  type AuthTokens,
  type AuthUser,
  type LoginResponse,
  type RegisterInput,
  type RegisterResponse,
} from '@teentack/shared';
import { post, refreshSession, tokenStore } from '@/lib/api-client';
import { AuthContext, type AuthContextValue } from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Restore the session from the stored refresh token on first paint.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = tokenStore.hasSession() ? await refreshSession() : null;
      if (!cancelled) {
        setUser(restored);
        setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const result = await post<LoginResponse>(
      '/auth/login',
      { identifier, password },
      { auth: false },
    );

    // Consumer accounts never take the MFA branch, but the type covers it.
    if (!result.mfaRequired) {
      tokenStore.set(result.tokens);
      setUser(result.user);
    }

    return result;
  }, []);

  const register = useCallback(
    (input: RegisterInput) => post<RegisterResponse>('/auth/register', input, { auth: false }),
    [],
  );

  const verifyOtp = useCallback(async (identifier: string, code: string) => {
    const result = await post<{ user: unknown; tokens?: AuthTokens }>(
      '/auth/verify-otp',
      { identifier, code },
      { auth: false },
    );

    const verified = authUserSchema.parse(result.user);

    // Verifying the signup code signs you in. A teen who is still PENDING
    // needs a session to reach the page that explains why. [FR-M01.2]
    if (result.tokens) {
      tokenStore.set(result.tokens);
      setUser(verified);
    }

    return verified;
  }, []);

  const logout = useCallback(async () => {
    try {
      await post<void>('/auth/logout', {});
    } finally {
      tokenStore.clear();
      setUser(null);
    }
  }, []);

  const reload = useCallback(async () => {
    setUser(await refreshSession());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, initializing, login, register, verifyOtp, logout, reload }),
    [user, initializing, login, register, verifyOtp, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
